import { MenuAction, TabModel } from '@anecdotes/design-system/dropdown-menu';
import { LoaderManagerService } from '@anecdotes/design-system/loaders';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  NgZone,
  OnDestroy,
  OnInit,
  Optional,
  SkipSelf,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  combineLatest,
  delay,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  fromEvent,
  map,
  Observable,
  of,
  shareReplay,
  take,
} from 'rxjs';
import { DOCUSIGN_SIGNING_COMPLETE_MESSAGE } from '../../../../constants';
import { RegularDateFormatMMMdy } from '@anecdotes/helpers';
import { TrustCenterModalService } from '../../../../services/trustcenter-modals/trustcenter-modals.service';
import { StandaloneAuthService } from '../../../../services/standalone-auth/standalone-auth.service';
import { getCurrentlyVisibleObjects } from '../../../../utils';
import {
  NdaResponse,
  RequirementsCategoryResponse,
  SettingsResponse,
  TrustCenterEventService,
  TrustCenterFacadeService,
  TrustCenterNavigationService,
  TrustCenterInfoResponse,
  TrustCenterStandaloneService,
  isStandaloneRef,
  settingsTabQueryParam,
  SettingsTabs,
  TrustCenterResourceTypes,
} from '@anecdotes/shared-trust-center';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { FocusingService } from '@anecdotes/focusing-mechanism';
import { SupportRequestService } from '@anecdotes/support';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

export enum Tabs {
  Controls = 'Controls',
  Documents = 'Documents',
  Subprocessors = 'Subprocessors',
  Policies = 'Policies',
  FAQs = 'FAQs',
}

const NDA_MODAL_RENDERING_EVENT = 'MODAL_VISIBLE';

const DOCUSIGN_SCRIPT_URL = 'https://demo.docusign.net/clickapi/sdk/latest/docusign-click.js';

@Component({
  selector: 'app-preview',
  templateUrl: './preview.component.html',
  styleUrls: ['./preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PreviewComponent implements OnInit, OnDestroy {
  @ViewChild('dsClickWrap')
  private set dsClickWrap(wrapElement: ElementRef<HTMLDivElement>) {
    this.isDsClickWrapRendered$.next(!!wrapElement);
  }

  @ViewChild('signingFrame')
  private set signingFrame(frameElement: ElementRef<HTMLIFrameElement>) {
    this.isSigningFrameRendered$.next(!!frameElement);
  }

  private readonly isSigningFrameRendered$ = new BehaviorSubject<boolean>(false);

  private readonly isDsClickWrapRendered$ = new BehaviorSubject<boolean>(false);

  private settings$: Observable<SettingsResponse>;

  readonly Tabs = Tabs;

  readonly loggedInMenuActions: MenuAction[] = [
    {
      id: 'logout',
      translationKey: this.buildTranslationKey('logout'),
      icon: 'user-menu/logout',
      iconClasses: 'logout-icon',
      action: () => this.logout(),
    },
  ];

  readonly dateFormat = RegularDateFormatMMMdy;

  readonly isPublishedPage = isStandaloneRef.isStandalone;

  @HostBinding('class.nda-loading')
  isNdaLoading = false;

  companyName: string;
  isLoggedIn: boolean;
  currentTabId: Tabs | null = null;
  isPreviewLink: boolean;
  isEmptyState$: Observable<boolean>;
  isLoadingFailed$: Observable<boolean>;
  loadingFailedError$: Observable<number>;
  trustCenterInfo$: Observable<TrustCenterInfoResponse>;
  editing$: Observable<boolean>;
  username$: Observable<string>;
  isPreviewMode$: Observable<boolean>;
  lastPublished$: Observable<Date | null>;
  hasPendingChanges$: Observable<boolean>;
  loggedIn$: Observable<boolean>;
  tabs$: Observable<TabModel[]>;
  hasPendingAccess$: Observable<boolean>;
  isAllLoaded$: Observable<boolean>;
  hasTrustCenterUrl$: Observable<boolean>;
  docusignSigningUrl$ = new BehaviorSubject<SafeResourceUrl | null>(null);

  get isBackToBrandingTab(): boolean {
    const tabId = this.router.routerState.snapshot.root.queryParams?.[settingsTabQueryParam] as SettingsTabs;
    return tabId === SettingsTabs.Branding;
  }

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly elementRef: ElementRef,
    @SkipSelf() private readonly loaderManager: LoaderManagerService,
    private readonly trustCenterFacadeService: TrustCenterFacadeService,
    private readonly trustCenterStandaloneService: TrustCenterStandaloneService,
    private readonly trustCenterModalService: TrustCenterModalService,
    private readonly trustCenterNavigationService: TrustCenterNavigationService,
    private readonly ngZone: NgZone,
    private readonly trustCenterEventService: TrustCenterEventService,
    private readonly destroyRef: DestroyRef,
    private readonly translate: TranslateService,
    private readonly router: Router,
    private readonly focusingService: FocusingService,
    private readonly domSanitizer: DomSanitizer,
    @Optional() private readonly supportRequestService: SupportRequestService,
    @Optional() private readonly standaloneAuthService: StandaloneAuthService,
  ) {}

  ngOnInit(): void {
    this.initializeValues();
    this.initializeListeners();

    if (this.isPublishedPage) {
      this.handlePublishedTrustCenterAuth();
    }
  }

  private initializeValues(): void {
    this.isPreviewLink = this.isPublishedPage ? this.trustCenterStandaloneService.isPreviewLink() : false;

    this.isLoadingFailed$ = this.isPublishedPage ? this.trustCenterFacadeService.isLoadingFailed() : of(false);

    this.loadingFailedError$ = this.isPublishedPage ? this.trustCenterFacadeService.getLoadingFailedErrorCode() : of(null);

    this.isEmptyState$ = this.isPublishedPage ? this.trustCenterFacadeService.isEmptyState() : of(false);

    this.trustCenterInfo$ = this.trustCenterFacadeService.getTrustCenterInfo();

    this.settings$ = this.trustCenterFacadeService.getSettings().pipe(shareReplay({ refCount: true, bufferSize: 1 }));

    this.lastPublished$ = this.settings$
      .pipe(
        map((settings) => (settings?.last_published ? new Date(`${settings.last_published}Z`) : null)),
      );

    this.hasPendingChanges$ = this.settings$.pipe(map((settings) => settings.pending_changes));

    this.editing$ = this.trustCenterFacadeService.isEditing();

    this.username$ = this.trustCenterFacadeService.getUsername();

    this.loggedIn$ = this.trustCenterFacadeService.isLoggedIn();

    this.hasPendingAccess$ = this.trustCenterFacadeService.hasPendingAccess();

    this.isPreviewMode$ = this.trustCenterFacadeService.isPreviewMode();

    this.hasTrustCenterUrl$ = this.trustCenterFacadeService.hasTrustCenterUrl();

    this.trustCenterFacadeService
      .getName()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((name) => {
        this.companyName = name;

        if (this.isPublishedPage && this.companyName) {
          this.trustCenterEventService.trackPublishedPageView(this.companyName);
        }
      });

    this.loggedIn$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((loggedIn) => {
      this.isLoggedIn = loggedIn;
    });

    this.initializeTrustCenter();

    this.initializeTabs();
  }

  private initializeTabs(): void {
    const documents$ = this.trustCenterFacadeService.getDocuments();

    const subprocessors$ = getCurrentlyVisibleObjects(
      this.editing$,
      this.trustCenterFacadeService.getSubprocessors(),
    );

    const policies$ = getCurrentlyVisibleObjects(
      this.editing$,
      this.trustCenterFacadeService.getPolicies(),
    );

    const categories$ = this.trustCenterFacadeService.getCategories()
      .pipe(
        map((categories: RequirementsCategoryResponse[]) => categories.filter(this.isCategoryVisible).length !== 0),
      );

    const faqs$ = this.trustCenterFacadeService.getFaqs();

    this.tabs$ = combineLatest([
      this.editing$,
      documents$,
      subprocessors$,
      policies$,
      categories$,
      faqs$,
    ]).pipe(
      map(([editing, documents, subprocessors, policies, categories, faqs]) => {
        const tabs: TabModel[] = [];

        if (categories || editing) {
          tabs.push({
            tabId: Tabs.Controls,
            translationKey: this.buildTranslationKey('controls.tab'),
          });
        }

        if (documents.length || editing) {
          tabs.push({
            tabId: Tabs.Documents,
            translationKey: this.buildTranslationKey('documents.tab'),
          });
        }

        if (policies.length || editing) {
          tabs.push({
            tabId: Tabs.Policies,
            translationKey: this.buildTranslationKey('policies.tab'),
          });
        }

        if (subprocessors.length || editing) {
          tabs.push({
            tabId: Tabs.Subprocessors,
            translationKey: this.buildTranslationKey('subprocessors.tab'),
          });
        }

        if (faqs.length || editing) {
          tabs.push({
            tabId: Tabs.FAQs,
            translationKey: this.buildTranslationKey('faqs.tab'),
          });
        }

        return tabs;
      }),
    );
  }

  private initializeListeners(): void {
    if (this.isPublishedPage) {
      this.handleNda();
    } else {
      this.handleFocusing();
    }

    combineLatest([
      this.isAllLoaded$.pipe(distinctUntilChanged()),
      this.tabs$.pipe(distinctUntilChanged((oldTabs, newTabs) => JSON.stringify(oldTabs) === JSON.stringify(newTabs))),
    ])
      // a small delay to ensure that the tabs are rendered before the selected tab is set
      .pipe(delay(100), takeUntilDestroyed(this.destroyRef))
      .subscribe(([loaded, tabs]) => {
        if (!tabs.length || !loaded) {
          return;
        }

        const tabIds = tabs.map((tab) => tab.tabId);
        const newTabId = tabIds[0];

        this.currentTabId = null;
        this.cd.detectChanges();

        this.currentTabId = newTabId;
        this.cd.detectChanges();
      });

    this.isAllLoaded$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isLoaded) => {
      if (isLoaded && this.isPublishedPage && this.isLoggedIn) {
        this.trustCenterEventService.trackVisitorLogin(this.companyName);
      }
    });
  }

  private async handlePublishedTrustCenterAuth(): Promise<void> {
    if (!this.standaloneAuthService.isAuth) {
      return;
    }

    const waitForDataLoading$ = this.isAllLoaded$.pipe(filter(Boolean));
    await firstValueFrom(waitForDataLoading$);

    this.loaderManager.hide();
    this.standaloneAuthService.handleAuth();
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.handleSigningMessage.bind(this));
    this.loaderManager.hide();
  }

  buildTranslationKey(key: string): string {
    return `trustCenter.${key}`;
  }

  onPreviewBtnClick(): void {
    this.trustCenterFacadeService.pauseEdit();
    this.scrollToTop();
  }

  async navigateToPublishedPage(): Promise<void> {
    const url = await firstValueFrom(this.trustCenterFacadeService.getTrustCenterUrl());
    window.open(`https://${url}`, '_blank');
  }

  navigateToSettingsBrandingTab(): void {
    this.trustCenterNavigationService.navigateToSettings(SettingsTabs.Branding);
  }

  onStopPreviewBtnClick(): void {
    if (this.isBackToBrandingTab) {
      this.navigateToSettingsBrandingTab();
      this.trustCenterFacadeService.resumeEdit();
    } else {
      this.trustCenterFacadeService.resumeEdit();
      this.scrollToTop();
    }
  }

  tabDisplaySelector(tab: TabModel): string | undefined {
    return tab.translationKey ? this.translate.instant(tab.translationKey) : undefined;
  }

  tabIdSelector(tab: TabModel): string {
    return tab.tabId;
  }

  onTabChange(tabId: Tabs): void {
    this.currentTabId = tabId;
    this.cd.detectChanges();
  }

  requestAccess(): void {
    this.trustCenterModalService.openRequestAccessModal();
  }

  contactUs(): void {
    this.trustCenterEventService.trackContactUsClick();

    this.supportRequestService.openTicketAsync({
      subject: this.translate.instant(this.buildTranslationKey('emptyState.ticket.subject')),
      description: '',
    });
  }

  async openPublishConfirmation(): Promise<void> {
    const editing = await firstValueFrom(this.editing$);
    const backToEditorCallback = editing ? undefined : this.onStopPreviewBtnClick.bind(this);

    this.trustCenterModalService.openPublishConfirmation(backToEditorCallback);
  }

  private scrollToTop(): void {
    this.elementRef.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private async handleNda(): Promise<void> {
    const nda = await firstValueFrom(this.trustCenterFacadeService.getNda().pipe(filter(Boolean)));

    if (nda?.needs_nda) {
      if (nda.nda.clickwrap_id) {
        await firstValueFrom(this.isDsClickWrapRendered$.pipe(filter(Boolean)));
        this.handleDocusignClickwrapModalLoading();
        await this.loadDocusign();
        this.showNda(nda);
      } else {
        if (nda.nda.document_data?.signingUrl) {
          this.handleDocusignEnvelopeModalLoading(nda.nda.document_data?.signingUrl);
        }
      }
    }
  }

  private loadDocusign(): Promise<void> {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = DOCUSIGN_SCRIPT_URL;
      script.addEventListener('load', () => resolve());
      this.elementRef.nativeElement.appendChild(script);
    });
  }

  private showNda(nda: NdaResponse): void {
    (window as any).docuSignClick.Clickwrap.render(
      {
        environment: nda.nda.environment,
        accountId: nda.nda.account_id,
        clickwrapId: nda.nda.clickwrap_id,
        clientUserId: nda.nda.client_user_id,
        documentData: nda.nda.document_data,
        onAgreed: () =>
          this.ngZone.run(() => {
            window.location.reload();
          }),
      },
      '#ds-clickwrap',
    );
  }

  private initializeTrustCenter(): void {
    if (this.isPublishedPage) {
      this.trustCenterFacadeService.initAsync();
    }

    this.isAllLoaded$ = this.trustCenterFacadeService.isEditorTabDataLoaded()
      .pipe(shareReplay({ refCount: true, bufferSize: 1 }));

    this.cd.detectChanges();
  }

  private async logout(): Promise<void> {
    await this.trustCenterFacadeService.signOut();
  }

  private isCategoryVisible(category: RequirementsCategoryResponse): boolean {
    return !!category.requirements.filter((requirement) => requirement.is_visible).length;
  }

  private async handleDocusignEnvelopeModalLoading(signingUrl: string): Promise<void> {
    this.docusignSigningUrl$.next(this.domSanitizer.bypassSecurityTrustResourceUrl(signingUrl));
    await firstValueFrom(this.isSigningFrameRendered$.pipe(filter(Boolean)));

    window.addEventListener('message', this.handleSigningMessage.bind(this));
  }

  private handleSigningMessage(event: MessageEvent): void {
    if (event.data === DOCUSIGN_SIGNING_COMPLETE_MESSAGE) {
      window.location.reload();
    }
  }

  private handleDocusignClickwrapModalLoading(): void {
    this.isNdaLoading = true;
    this.cd.detectChanges();

    fromEvent(window, 'message')
      .pipe(
        filter((messageEvent: MessageEvent) => messageEvent.data?.type === NDA_MODAL_RENDERING_EVENT),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.isNdaLoading = false;
        this.cd.detectChanges();
      });
  }

  private handleFocusing(): void {
    this.focusingService.getFocusingStreamForResource(TrustCenterResourceTypes.Framework)
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(async (frameworkId: string) => {
        await this.waitForLoading();
        this.trustCenterModalService.openFrameworkEditSidePanel(frameworkId);
        this.focusingService.finishFocusing(TrustCenterResourceTypes.Framework);
      });

    this.focusingService.getFocusingStreamForResource(TrustCenterResourceTypes.Document)
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(async (documentId: string) => {
        await this.waitForLoading();
        this.trustCenterModalService.openDocumentEditSidePanel(documentId);
        this.onTabChange(Tabs.Documents);
        this.focusingService.finishFocusing(TrustCenterResourceTypes.Document);
      });
  }

  private async waitForLoading(): Promise<void> {
    await firstValueFrom(this.isAllLoaded$.pipe(filter(Boolean)));
  }
}
